import {buildPhotoFileName, jpegFromBase64} from '../capture/photo'
import {canSharePhoto, sharePhoto} from '../capture/share'
import {CAPTURE_FLASH_MS, CAPTURE_FLASH_PEAK_OPACITY, CAPTURE_RING_MS, UI_TEXT} from '../config'
import {byId} from './dom'

/**
 * The shutter button and the preview sheet.
 *
 * Same split as `createHud`: the markup lives in `index.html`, this file only
 * drives it. It knows nothing about XR8 or three.js - the one thing it is handed
 * is a `takePhoto` thunk that resolves base64 JPEG bytes.
 *
 * The preview is not a nicety. `navigator.share()` needs transient user
 * activation, so a photo cannot be captured and shared off the same tap; the
 * preview is what turns the share into its own gesture. See `../capture/share`.
 */

/** Mirrors the ids in `index.html`. */
const ELEMENT_IDS = {
  shutter: 'capture-shutter',
  ring: 'capture-ring',
  flash: 'capture-flash',
  preview: 'capture-preview',
  image: 'capture-image',
  saveHint: 'capture-savehint',
  close: 'capture-close',
  share: 'capture-share',
} as const

export interface CapturedPhotoInfo {
  readonly fileName: string
  readonly bytes: number
  readonly canShare: boolean
  /**
   * Tap to the first painted frame, in ms - i.e. how long the flash was on
   * screen before the capture was allowed to block anything.
   */
  readonly paintDelayMs: number
  /**
   * How long the engine's read-back took, in ms. Compare against
   * `CAPTURE_FLASH_MS`: whenever this is the larger number, the main thread was
   * frozen for the rest of the flash, which is the behaviour `afterNextPaint`
   * exists to keep survivable.
   */
  readonly captureMs: number
}

export interface CaptureUiDeps {
  /** Resolves base64 JPEG bytes with the `data:` prefix already stripped. */
  readonly takePhoto: () => Promise<string>
  readonly onError: (error: unknown) => void
  /**
   * Fired synchronously on the shutter tap, before anything is captured. Exists
   * for the shutter sound: playing it needs an AudioContext, an AudioContext
   * needs THREE, and this file stays free of both. Must not be given anything
   * slow - it runs on the tap, in front of the flash.
   */
  readonly onShutter?: () => void
  /** Called once per successful capture. Used for the `?debug=1` readout. */
  readonly onCaptured?: (info: CapturedPhotoInfo) => void
}

export interface CaptureUi {
  /**
   * Reveals or hides the shutter. Left off until something has been placed - a
   * photo of an empty floor is not worth offering, and the button would compete
   * with the "tap to place" prompt.
   */
  setAvailable(available: boolean): void
  dispose(): void
}

export const createCaptureUi = ({
  takePhoto,
  onError,
  onShutter,
  onCaptured,
}: CaptureUiDeps): CaptureUi => {
  const shutter = byId(ELEMENT_IDS.shutter, HTMLButtonElement)
  const ringElement = byId(ELEMENT_IDS.ring, HTMLSpanElement)
  const flashElement = byId(ELEMENT_IDS.flash, HTMLDivElement)
  const preview = byId(ELEMENT_IDS.preview, HTMLDivElement)
  const image = byId(ELEMENT_IDS.image, HTMLImageElement)
  const saveHint = byId(ELEMENT_IDS.saveHint, HTMLParagraphElement)
  const closeButton = byId(ELEMENT_IDS.close, HTMLButtonElement)
  const shareButton = byId(ELEMENT_IDS.share, HTMLButtonElement)

  const abort = new AbortController()
  const listen = (target: HTMLElement, handler: () => void): void => {
    target.addEventListener('click', handler, {signal: abort.signal})
  }

  let currentFile: File | null = null
  let currentUrl: string | null = null
  let flashAnimation: Animation | null = null

  /**
   * Read once. A media query that changes mid-session is not worth a listener
   * here: the setting is an OS-level preference, and the capture UI is rebuilt
   * on every camera attach anyway.
   */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  /**
   * Runs `task` after the browser has actually put a frame on the screen.
   *
   * Two hops, not one. A rAF registered from a click handler runs in that same
   * frame's rAF phase, which is still ahead of the frame's paint; only the
   * second callback is guaranteed to be standing on something the user saw.
   */
  const afterNextPaint = (task: () => void): void => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (abort.signal.aborted) return
        task()
      })
    })
  }

  /**
   * Web Animations rather than a CSS class, so the duration exists in exactly
   * one place (`config.ts`) and cannot drift away from a mirrored stylesheet
   * value.
   *
   * The keyframes hold the peak for the first 30% instead of decaying from
   * frame one. On a phone this animation gets very few frames of its own (see
   * `onShutterClick`), and whichever ones it does get have to be the bright
   * ones or there is no flash to speak of.
   */
  const flash = (): void => {
    // Promoted for the run only. The capture freezes the main thread partway
    // through this animation, and a composited opacity animation is ticked by
    // the compositor's own clock, so promoting it is what gives the fade a
    // chance to keep running through the freeze rather than resuming after it.
    // Not permanent: a full-screen layer is on the order of 12 MB of texture on
    // a current phone, which is not worth holding between shots.
    flashElement.style.willChange = 'opacity'
    const peak = prefersReducedMotion ? CAPTURE_FLASH_PEAK_OPACITY / 2 : CAPTURE_FLASH_PEAK_OPACITY
    const animation = flashElement.animate(
      [
        {opacity: peak, offset: 0},
        {opacity: peak, offset: 0.3},
        {opacity: 0, offset: 1},
      ],
      {duration: CAPTURE_FLASH_MS, easing: 'ease-out'}
    )
    flashAnimation = animation
    // Rejects if the animation is ever cancelled, which is not worth a line.
    void animation.finished
      .catch(() => {})
      .finally(() => {
        // A later flash may already own the layer.
        if (flashAnimation === animation) flashElement.style.willChange = ''
      })
  }

  /**
   * The ring that expands out of the button.
   *
   * Rides on the same tap as the flash rather than on `:active`, so the two
   * cannot disagree about whether a press counted. Under reduced motion it
   * fades in place - the point is confirming the tap, and dropping the
   * confirmation entirely would be the wrong reading of the preference.
   */
  const ring = (): void => {
    ringElement.animate(
      prefersReducedMotion
        ? [{opacity: 0.95}, {opacity: 0}]
        : [
            {transform: 'scale(1)', opacity: 0.95},
            {transform: 'scale(1.9)', opacity: 0},
          ],
      {duration: CAPTURE_RING_MS, easing: 'cubic-bezier(.2, .7, .3, 1)'}
    )
  }

  const releaseCurrent = (): void => {
    currentFile = null
    if (currentUrl === null) return
    // The attribute goes first. Revoking a blob URL that an <img> still points
    // at makes WebKit drop the decoded image, which shows up as the preview
    // going blank in the frame before it closes.
    image.removeAttribute('src')
    URL.revokeObjectURL(currentUrl)
    currentUrl = null
  }

  const closePreview = (): void => {
    preview.hidden = true
    shareButton.hidden = true
    saveHint.hidden = true
    releaseCurrent()
    shutter.disabled = false
  }

  const openPreview = (file: File): boolean => {
    releaseCurrent()
    currentFile = file
    currentUrl = URL.createObjectURL(file)
    image.src = currentUrl

    // Either a share button or a long-press instruction, never both and never
    // neither: with no Web Share API the long press is the only way to keep the
    // photo, so the user has to be told it exists.
    const shareable = canSharePhoto(file)
    shareButton.hidden = !shareable
    saveHint.hidden = shareable
    saveHint.textContent = shareable ? '' : UI_TEXT.saveByLongPress

    preview.hidden = false
    return shareable
  }

  const onShutterClick = (): void => {
    if (shutter.disabled) return
    shutter.disabled = true

    const firedAt = performance.now()
    flash()
    ring()
    // Synchronous, and it has to be: the first call is what unlocks the
    // AudioContext, and that only works while this tap's activation is live.
    onShutter?.()

    // The capture is NOT started on this tap, and that delay is the whole
    // reason the flash is visible at all. It used to be started here, and the
    // flash was never once seen. Why, in order:
    //
    //  1. `XR8.CanvasScreenshot.takeScreenshot` does its drawImage and toDataURL
    //     of a 1920px canvas synchronously inside a rAF callback, which runs
    //     BEFORE that frame is painted. On a phone that is a few hundred ms with
    //     the main thread held.
    //  2. The document timeline does not advance while it runs, so the flash is
    //     still at currentTime 0 when the block lifts. Its first painted frame
    //     is the blocking frame itself.
    //  3. That is also the frame the capture resolves on. `openPreview()` runs
    //     in the microtask checkpoint at the end of the same task, i.e. still
    //     ahead of that frame's paint - so the opaque preview sheet goes up on
    //     the exact frame the white first appears, and the flash plays out its
    //     whole duration underneath it.
    //
    // Deferring past a paint breaks step 3: the white is on screen before
    // anything blocks, so the preview can no longer beat it there. What the
    // freeze does to the rest of the fade is then a browser detail that does not
    // matter much - composited, it carries on through; not composited, the
    // whiteout simply holds until the preview opens. Both read as a shutter.
    //
    // Safe to defer because `takeScreenshot` needs no user activation. The call
    // that does is `navigator.share()`, and the preview exists precisely to give
    // it a tap of its own.
    afterNextPaint(() => {
      const captureStartedAt = performance.now()
      takePhoto()
        .then((base64) => {
          const fileName = buildPhotoFileName(new Date())
          const file = jpegFromBase64(base64, fileName)
          const canShare = openPreview(file)
          onCaptured?.({
            fileName,
            bytes: file.size,
            canShare,
            paintDelayMs: Math.round(captureStartedAt - firedAt),
            captureMs: Math.round(performance.now() - captureStartedAt),
          })
        })
        .catch((error: unknown) => {
          shutter.disabled = false
          onError(error)
        })
    })
  }

  const onShareClick = (): void => {
    const file = currentFile
    if (file === null) return
    // Nothing may be awaited in front of this call. `sharePhoto` reaches
    // `navigator.share()` synchronously, which is the only reason the click's
    // transient activation is still valid when iOS checks for it.
    sharePhoto(file)
      .then((outcome) => {
        // 'cancelled' leaves the preview up: the user dismissed the sheet, they
        // did not ask to throw the photo away.
        if (outcome === 'shared') closePreview()
      })
      .catch(onError)
  }

  listen(shutter, onShutterClick)
  listen(closeButton, closePreview)
  listen(shareButton, onShareClick)

  return {
    setAvailable: (available) => {
      shutter.hidden = !available
    },
    dispose: () => {
      abort.abort()
      closePreview()
      shutter.hidden = true
    },
  }
}
